import type { ProductBadge, ProductType } from '@emazy/shared-types';

import type { CatalogSort, PublicProductListInput } from '../api/types';

const badges = new Set<ProductBadge>(['bestseller', 'new', 'popular', 'seasonal']);
const productTypes = new Set<ProductType>(['wood-engraving', 'ornament', 'tshirt']);
const sorts = new Set<CatalogSort>(['featured', 'newest']);

export type CatalogUrlState = {
  category?: string;
  productType?: ProductType;
  featured?: boolean;
  badge?: ProductBadge;
  sort: CatalogSort;
  limit: number;
};

export function parseCatalogUrlState(search: URLSearchParams): CatalogUrlState {
  const category = validSlug(search.get('category'));
  const productType = search.get('productType');
  const badge = search.get('badge');
  const sort = search.get('sort');
  const rawLimit = Number(search.get('limit'));
  const limit = Number.isInteger(rawLimit) && rawLimit >= 1 && rawLimit <= 48 ? rawLimit : 12;
  return {
    ...(category ? { category } : {}),
    ...(productType && productTypes.has(productType as ProductType)
      ? { productType: productType as ProductType }
      : {}),
    ...(search.get('featured') === 'true' ? { featured: true } : {}),
    ...(badge && badges.has(badge as ProductBadge) ? { badge: badge as ProductBadge } : {}),
    sort: sort && sorts.has(sort as CatalogSort) ? (sort as CatalogSort) : 'featured',
    limit,
  };
}

export function toProductListInput(state: CatalogUrlState): PublicProductListInput {
  return {
    ...(state.category ? { category: state.category } : {}),
    ...(state.productType ? { productType: state.productType } : {}),
    ...(state.featured ? { featured: true } : {}),
    ...(state.badge ? { badge: state.badge } : {}),
    sort: state.sort,
    limit: state.limit,
  };
}

export function updateCatalogParam(
  current: URLSearchParams,
  key: 'category' | 'productType' | 'featured' | 'badge' | 'sort' | 'limit',
  value: string,
) {
  const next = new URLSearchParams(current);
  if (value && !(key === 'sort' && value === 'featured') && !(key === 'limit' && value === '12'))
    next.set(key, value);
  else next.delete(key);
  return next;
}

function validSlug(value: string | null) {
  return value && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) ? value : undefined;
}
